"use client"

import { useAuthClient, useUser } from "@sophy-ai/auth/nextjs/client"
import { Button } from "@sophy-ai/ui/button"
import {
  ArrowRight,
  BarChart3,
  Brain,
  Clock,
  FileText,
  LogOut,
  MessageSquare,
  Target,
  TrendingUp,
  Users,
} from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { allTools } from "./_data/tools"

// 通知カード共通コンポーネント（デスクトップ版）
interface NotificationCardProps {
  id: string
  title: string
  message: string
  icon: {
    type: "image" | "svg"
    src?: string
    alt?: string
    svg?: React.ReactNode
    bgColor?: string
  }
  index: number
  animationDirection: "left" | "right"
  animationDuration: string
}

function DesktopNotificationCard({
  id,
  title,
  message,
  icon,
  index,
  animationDirection,
  animationDuration,
}: NotificationCardProps) {
  return (
    <div
      key={`${id}-${index}`}
      className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-2 animate-float border border-gray-200 w-40 xl:w-52 z-20"
      style={{
        animationDelay: "0s, 0s",
        animationDuration: `${animationDuration}, 0.6s`,
        animation: `float ${animationDuration} ease-in-out infinite, notification-entrance-${animationDirection} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
      }}
    >
      <div className="flex items-start gap-1.5 xl:gap-2">
        <div
          className={`w-6 h-6 xl:w-8 xl:h-8 rounded-lg flex items-center justify-center p-0.5 xl:p-1 flex-shrink-0 ${
            icon.bgColor || "bg-white/80"
          } ${icon.type === "image" ? "border border-gray-200" : ""}`}
        >
          {icon.type === "image" ? (
            <Image src={icon.src!} alt={icon.alt!} width={40} height={40} className="w-full h-full object-contain" />
          ) : (
            icon.svg
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] xl:text-xs font-semibold mb-0.5" style={{ color: "#26251f" }}>
            {title}
          </p>
          <p className="text-[9px] xl:text-[10px] line-clamp-2" style={{ color: "#26251f" }}>
            {message}
          </p>
        </div>
      </div>
    </div>
  )
}

// 通知カード共通コンポーネント（モバイル版）
function MobileNotificationCard({ id, title, message, icon, index, animationDirection }: NotificationCardProps) {
  return (
    <div
      key={`${id}-mobile-${index}`}
      className={`backdrop-blur-md rounded-xl shadow-lg animate-notification-entrance-${animationDirection} border border-gray-200 z-20`}
      style={{
        background: "rgba(255, 255, 255, 0.8)",
        width: "clamp(110px, 28vw, 220px)",
        padding: "clamp(6px, 1.6vw, 14px)",
      }}
    >
      <div className="flex items-start gap-1.5">
        <div
          className={`rounded-lg flex items-center justify-center flex-shrink-0 ${
            icon.bgColor || "bg-white/80"
          } ${icon.type === "image" ? "border border-gray-200" : ""}`}
          style={{
            width: "clamp(18px, 5vw, 32px)",
            height: "clamp(18px, 5vw, 32px)",
            padding: "2px",
            background: icon.type === "image" ? "rgba(255, 255, 255, 0.8)" : icon.bgColor || "rgba(255, 255, 255, 0.8)",
          }}
        >
          {icon.type === "image" ? (
            <Image src={icon.src!} alt={icon.alt!} width={40} height={40} className="w-full h-full object-contain" />
          ) : (
            icon.svg
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p
            className="font-semibold text-left"
            style={{
              color: "#26251f",
              fontSize: "clamp(9px, 2.4vw, 14px)",
              marginBottom: "2px",
            }}
          >
            {title}
          </p>
          <p
            className="line-clamp-2 text-left"
            style={{
              color: "#666",
              fontSize: "clamp(8px, 2.1vw, 12px)",
              lineHeight: "1.3",
            }}
          >
            {message}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function LandingPage() {
  const { isSignedIn, isLoaded } = useUser()
  const { signOut } = useAuthClient()
  const router = useRouter()

  const [language, setLanguage] = useState<"ja" | "en" | "zh">("ja")
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set())
  const [thinkingCards, setThinkingCards] = useState<Set<string>>(new Set())
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [isComingSoonActive, setIsComingSoonActive] = useState(false)
  const [notificationIndex, setNotificationIndex] = useState({
    slack: 0,
    calendar: 0,
    linear: 0,
    notion: 0,
    github: 0,
  })

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("sophy-language") as "ja" | "en" | "zh" | null
      if (saved) setLanguage(saved)
    }
  }, [])

  // 通知メッセージを定期的に切り替え（各カード少しずつずらす）
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = []

    // Slack - 15秒ごと
    intervals.push(
      setInterval(() => {
        setNotificationIndex((prev) => ({
          ...prev,
          slack: (prev.slack + 1) % 5,
        }))
      }, 15000)
    )

    // Calendar - 15秒ごと（2.5秒遅れ）
    setTimeout(() => {
      intervals.push(
        setInterval(() => {
          setNotificationIndex((prev) => ({
            ...prev,
            calendar: (prev.calendar + 1) % 5,
          }))
        }, 15000)
      )
    }, 2500)

    // Linear - 15秒ごと（5秒遅れ）
    setTimeout(() => {
      intervals.push(
        setInterval(() => {
          setNotificationIndex((prev) => ({
            ...prev,
            linear: (prev.linear + 1) % 5,
          }))
        }, 15000)
      )
    }, 5000)

    // Notion - 15秒ごと（7.5秒遅れ）
    setTimeout(() => {
      intervals.push(
        setInterval(() => {
          setNotificationIndex((prev) => ({
            ...prev,
            notion: (prev.notion + 1) % 5,
          }))
        }, 15000)
      )
    }, 7500)

    // GitHub - 15秒ごと（10秒遅れ）
    setTimeout(() => {
      intervals.push(
        setInterval(() => {
          setNotificationIndex((prev) => ({
            ...prev,
            github: (prev.github + 1) % 5,
          }))
        }, 15000)
      )
    }, 10000)

    return () => intervals.forEach(clearInterval)
  }, [])

  const t = (ja: string, en: string, zh: string) => {
    if (language === "zh") return zh
    return language === "ja" ? ja : en
  }

  // 通知メッセージのバリエーション
  const notifications = {
    slack: [
      {
        ja: "田中さん: おつかれさまです！例の資料、確認しました。すごく分かりやすかったです👍",
        en: "Sarah: Great work on that document! Super clear and helpful 👍",
        zh: "李明: 刚看了文档,写得很清楚!辛苦了👍",
      },
      {
        ja: "佐藤さん: 明日のミーティング、30分早めてもらえますか？急な予定が入ってしまって🙏",
        en: "Mike: Can we move tomorrow's meeting 30min earlier? Something urgent came up 🙏",
        zh: "王静: 明天的会能提前30分钟吗?临时有个急事🙏",
      },
      {
        ja: "山田さん: 今週の進捗会議の資料、共有しておきました。確認お願いします！",
        en: "Emily: Shared this week's progress deck. Please take a look!",
        zh: "张伟: 本周进度报告已共享,请查看!",
      },
      {
        ja: "鈴木さん: ランチ一緒に行きませんか？新しいお店見つけたんです🍜",
        en: "James: Want to grab lunch? Found an awesome new spot 🍔",
        zh: "刘芳: 一起吃午饭吗?发现了家不错的新店🍜",
      },
      {
        ja: "高橋さん: お疲れ様です。先ほどのプレゼン、とても良かったです！",
        en: "Alex: Great job on that presentation earlier!",
        zh: "陈强: 辛苦了!刚才的演示很棒!",
      },
    ],
    calendar: [
      {
        ja: "15分後: デザインレビューミーティング",
        en: "15min: Design Review Meeting",
        zh: "15分钟后: 设计评审会议",
      },
      {
        ja: "30分後: 1on1 with 田中さん",
        en: "30min: 1-on-1 with Sarah",
        zh: "30分钟后: 与李明一对一",
      },
      {
        ja: "1時間後: クライアントプレゼン",
        en: "1hr: Client Presentation",
        zh: "1小时后: 客户演示",
      },
      {
        ja: "明日 10:00: 週次全体会議",
        en: "Tomorrow 10:00 AM: Weekly All-Hands",
        zh: "明天 上午10:00: 每周全员会议",
      },
      {
        ja: "今日 17:00: チーム振り返り会",
        en: "Today 5:00 PM: Team Retrospective",
        zh: "今天 下午5:00: 团队回顾会",
      },
    ],
    linear: [
      {
        ja: "佐藤さんがタスク「ランディングページ最適化」を完了しました",
        en: 'Mike completed "Landing page optimization"',
        zh: '王静完成了"落地页优化"任务',
      },
      {
        ja: "山田さんが「API統合」をレビュー待ちにしました",
        en: 'Emily moved "API integration" to review',
        zh: '张伟将"API集成"移至待审核',
      },
      {
        ja: "新しいタスク「ユーザーフィードバック分析」が割り当てられました",
        en: 'New task "User feedback analysis" assigned to you',
        zh: '新任务"用户反馈分析"已分配给你',
      },
      {
        ja: "高橋さんが「モバイル対応」の優先度を高に変更しました",
        en: 'Alex raised priority of "Mobile support" to high',
        zh: '陈强将"移动端支持"优先级提高至高',
      },
      {
        ja: "スプリント終了まであと3日。残り5タスク",
        en: "Sprint ends in 3 days. 5 tasks remaining",
        zh: "冲刺还有3天结束。剩余5个任务",
      },
    ],
    notion: [
      {
        ja: "山田さんが「Q1マーケティング戦略」を更新しました",
        en: 'Emily updated "Q1 Marketing Strategy"',
        zh: '张伟更新了"Q1营销策略"',
      },
      {
        ja: "新しいページ「製品ロードマップ2025」が作成されました",
        en: 'New page "Product Roadmap 2025" created',
        zh: '创建了新页面"2025产品路线图"',
      },
      {
        ja: "鈴木さんが議事録にコメントを追加しました",
        en: "James added a comment to meeting notes",
        zh: "刘芳在会议记录中添加了评论",
      },
      {
        ja: "「開発ガイドライン」が更新されました - 3箇所変更",
        en: '"Dev Guidelines" updated - 3 changes',
        zh: '"开发指南"已更新 - 3处修改',
      },
      {
        ja: "田中さんがあなたをページにメンションしました",
        en: "Sarah mentioned you in a page",
        zh: "李明在页面中提到了你",
      },
    ],
    github: [
      {
        ja: "PR #127「ユーザー認証機能の追加」があなたのレビュー待ちです",
        en: 'PR #127 "Add user auth" needs your review',
        zh: 'PR #127 "添加用户认证"等待你审核',
      },
      {
        ja: "山田さんがあなたのPRを承認しました ✅",
        en: "Emily approved your PR ✅",
        zh: "张伟批准了你的PR ✅",
      },
      {
        ja: "Issue #45にコメントがつきました",
        en: "New comment on Issue #45",
        zh: "Issue #45有新评论",
      },
      {
        ja: "main ブランチに3つの新しいコミットがあります",
        en: "3 new commits on main branch",
        zh: "main分支有3个新提交",
      },
      {
        ja: "CI/CDが失敗しました - feature/new-dashboard",
        en: "CI/CD failed - feature/new-dashboard",
        zh: "CI/CD失败 - feature/new-dashboard",
      },
    ],
  }

  // 通知カード設定データ
  const notificationCards = [
    {
      id: "slack",
      title: "Slack",
      icon: {
        type: "image" as const,
        src: "https://a.slack-edge.com/80588/marketing/img/icons/icon_slack_hash_colored.png",
        alt: "Slack",
      },
      position: {
        desktop: "left-[-2rem] md:left-[-2.5rem] lg:left-[-3rem] xl:left-[-4rem] top-[5%]",
        mobile: "top-[5%] left-[-8vw] min-[480px]:left-[-10vw] sm:left-[-12vw]",
      },
      animationDirection: "left" as const,
      animationDuration: "4s",
    },
    {
      id: "calendar",
      title: "Google",
      icon: {
        type: "image" as const,
        src: "https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png",
        alt: "Google Calendar",
      },
      position: {
        desktop: "right-[-2rem] top-[8%]",
        mobile: "top-[8%] right-[-8vw] min-[480px]:right-[-10vw] sm:right-[-12vw]",
      },
      animationDirection: "right" as const,
      animationDuration: "5s",
    },
    {
      id: "linear",
      title: "Linear",
      icon: {
        type: "svg" as const,
        bgColor: "bg-black",
        svg: (
          <svg viewBox="0 0 100 100" className="w-full h-full" fill="white">
            <path d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z" />
          </svg>
        ),
      },
      position: {
        desktop: "right-[-5rem] top-[38.2%]",
        mobile: "top-[38.2%] right-[-8vw] min-[480px]:right-[-10vw] sm:right-[-12vw]",
      },
      animationDirection: "right" as const,
      animationDuration: "4.5s",
    },
    {
      id: "notion",
      title: "Notion",
      icon: {
        type: "image" as const,
        src: "https://upload.wikimedia.org/wikipedia/commons/e/e9/Notion-logo.svg",
        alt: "Notion",
      },
      position: {
        desktop: "left-[-3rem] top-[65%]",
        mobile: "top-[61.8%] left-[-8vw] min-[480px]:left-[-10vw] sm:left-[-12vw]",
      },
      animationDirection: "left" as const,
      animationDuration: "4.5s",
    },
    {
      id: "github",
      title: "GitHub",
      icon: {
        type: "image" as const,
        src: "https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png",
        alt: "GitHub",
      },
      position: {
        desktop: "right-[-1rem] top-[70%]",
        mobile: "top-[70%] right-[-8vw] min-[480px]:right-[-10vw] sm:right-[-12vw]",
      },
      animationDirection: "right" as const,
      animationDuration: "5.5s",
    },
  ]

  const toggleCard = (cardId: string) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(cardId)) {
        newSet.delete(cardId)
        setThinkingCards((prevThinking) => {
          const newThinking = new Set(prevThinking)
          newThinking.delete(cardId)
          return newThinking
        })
      } else {
        newSet.add(cardId)
        setThinkingCards((prevThinking) => {
          const newThinking = new Set(prevThinking)
          newThinking.add(cardId)
          return newThinking
        })
        setTimeout(() => {
          setThinkingCards((prevThinking) => {
            const newThinking = new Set(prevThinking)
            newThinking.delete(cardId)
            return newThinking
          })
        }, 1500)
      }
      return newSet
    })
  }

  const problemCards = {
    executive: [
      {
        icon: BarChart3,
        title: t("全体の生産性が見えない", "Can't see overall productivity", "无法看到整体生产力"),
        problem: t(
          "各チームの進捗や成果を統合的に把握できず、戦略的な意思決定が困難",
          "Hard to track team progress and make strategic decisions",
          "难以综合掌握各团队进度和成果，战略决策困难"
        ),
        solution: t(
          "リアルタイムダッシュボードで全チームの進捗・成果を統合的に可視化。データドリブンな戦略的意思決定を実現します。",
          "Real-time dashboard visualizes all team progress and outcomes. Enables data-driven strategic decision-making.",
          "实时仪表板综合可视化所有团队的进度和成果。实现数据驱动的战略决策。"
        ),
      },
      {
        icon: TrendingUp,
        title: t("組織の健康状態が不透明", "Unclear organization health", "组织健康状况不透明"),
        problem: t(
          "チームのモチベーションやストレス状態を把握できず、離職リスクに気づけない",
          "Can't track team motivation and stress, missing turnover risks",
          "无法掌握团队动力和压力状态，无法注意到离职风险"
        ),
        solution: t(
          "AIが各メンバーの状態を分析し、早期にリスクを検知。組織全体の健康状態を可視化し、予防的な施策を提案します。",
          "AI analyzes member states to detect risks early. Visualizes overall organization health and suggests preventive measures.",
          "AI分析各成员状态，及早发现风险。可视化整体组织健康状况，提出预防性措施。"
        ),
      },
    ],
    manager: [
      {
        icon: Target,
        title: t("個々のメンバー管理に時間がかかる", "Time-consuming member management", "成员管理耗时"),
        problem: t(
          "1on1の準備や進捗確認に多くの時間を取られ、本質的なマネジメントに集中できない",
          "Too much time on 1-on-1 prep and progress checks, can't focus on essential management",
          "在一对一准备和进度确认上花费大量时间，无法专注于本质的管理工作"
        ),
        solution: t(
          "AIが自動で各メンバーの状況を分析・要約。重要なポイントだけを提示し、マネジメント時間を50%削減します。",
          "AI automatically analyzes and summarizes member situations. Presents only key points, reducing management time by 50%.",
          "AI自动分析和总结各成员情况。仅呈现关键点，将管理时间减少50%。"
        ),
      },
      {
        icon: MessageSquare,
        title: t("適切なフィードバックができない", "Difficult to give proper feedback", "难以给予恰当的反馈"),
        problem: t(
          "メンバーごとに最適なコミュニケーション方法がわからず、フィードバックが効果的でない",
          "Don't know optimal communication for each member, feedback ineffective",
          "不了解每个成员的最佳沟通方法，反馈效果不佳"
        ),
        solution: t(
          "各メンバーの性格・価値観をAIが分析し、最適なフィードバック方法を提案。より効果的なコミュニケーションを実現します。",
          "AI analyzes each member's personality and values, suggests optimal feedback methods. Enables more effective communication.",
          "AI分析每个成员的性格和价值观，提出最佳反馈方法。实现更有效的沟通。"
        ),
      },
    ],
    member: [
      {
        icon: Clock,
        title: t("タスク管理に追われる", "Overwhelmed by task management", "被任务管理压得喘不过气"),
        problem: t(
          "複数のプロジェクトやタスクの優先順位付けに悩み、本来の業務に集中できない",
          "Struggling with prioritizing multiple projects and tasks, can't focus on core work",
          "难以确定多个项目和任务的优先顺序，无法专注于核心工作"
        ),
        solution: t(
          "AIがあなたの業務状況を把握し、最適な優先順位を提案。タスク管理の負担を減らし、本質的な仕事に集中できます。",
          "AI understands your work situation and suggests optimal priorities. Reduces task management burden, helps focus on essential work.",
          "AI掌握您的工作情况，提出最佳优先顺序。减轻任务管理负担，帮助您专注于本质工作。"
        ),
      },
      {
        icon: Brain,
        title: t("成長の方向性が見えない", "Unclear growth direction", "看不清成长方向"),
        problem: t(
          "自分の強みや弱みが客観的にわからず、どうキャリアを進めるべきか悩んでいる",
          "Can't objectively see strengths and weaknesses, unsure how to advance career",
          "无法客观了解自己的优势和劣势，不知道如何推进职业生涯"
        ),
        solution: t(
          "あなたの業務データとフィードバックをAIが分析し、客観的な強み・弱みを可視化。最適な成長パスを提案します。",
          "AI analyzes your work data and feedback, visualizes objective strengths and weaknesses. Suggests optimal growth path.",
          "AI分析您的工作数据和反馈，可视化客观的优劣势。提出最佳成长路径。"
        ),
      },
    ],
  }

  const faqItems = [
    {
      question: t(
        "ChatGPT、Claude、Geminiではなく、なぜSophyを使うべきですか？",
        "Why should I use Sophy instead of ChatGPT, Claude, or Gemini?",
        "为什么应该使用Sophy而不是ChatGPT、Claude或Gemini？"
      ),
      answer: t(
        "ChatGPT、Claude、Geminiは汎用的な会話AIですが、Sophyはあなた専用のパーソナライズAIです。あなたの仕事のやり方、チームの雰囲気、これまでの経験を深く理解し、個人とチームの両方に合わせたアドバイスや提案をしてくれます。個人のタスク管理からチーム全体のサポートまで、あなたのソフィーがすべてを一貫してお手伝いする点が大きな違いです。",
        "While ChatGPT, Claude, and Gemini are general-purpose conversational AIs, Sophy is your personalized AI. It deeply understands your work style, team atmosphere, and past experiences to provide personalized advice and suggestions for both individuals and teams. The key difference is that your Sophy consistently helps with everything from personal task management to full team support.",
        "ChatGPT、Claude和Gemini是通用型对话AI，而Sophy是您的专属个性化AI。它深入理解您的工作方式、团队氛围和过往经验，为个人和团队提供量身定制的建议和提案。关键区别在于，您的Sophy会从个人任务管理到整个团队支持，始终如一地为您提供帮助。"
      ),
    },
    {
      question: t("Sophyはどのような人に向いていますか？", "Who is Sophy suitable for?", "Sophy适合哪些人？"),
      answer: t(
        "個人のフリーランスから大企業のチームまで、もっと楽に効率よく働きたい全ての人に使っていただけます。自分の仕事をスムーズに進めたい個人、チームをうまくまとめたいマネージャー、会社全体の様子を把握したい経営層まで、それぞれの使い方に合わせて調整されます。",
        "Suitable for everyone from individual freelancers to enterprise teams who want to work more efficiently and comfortably. Works for individuals wanting smoother workflows, managers looking to coordinate their teams better, and executives wanting to understand company-wide situations. Adjusts to each person's way of working.",
        "从个人自由职业者到大型企业团队，所有希望更轻松高效工作的人都可以使用。无论是想要顺畅推进工作的个人、想要很好地管理团队的经理，还是想要掌握整个公司情况的管理层，都会根据各自的使用方式进行调整。"
      ),
    },
    {
      question: t(
        "始めるのにどのくらい時間がかかりますか？",
        "How long does it take to get started?",
        "需要多长时间才能开始使用？"
      ),
      answer: t(
        "個人で使うなら、今すぐ始められます。アカウントを作ったら、すぐにあなた専用のソフィーが仕事のサポートを始めてくれます。チームで使う場合も、基本的な設定は10分くらいで終わり、メンバーを招待しても1日あればみんなで使い始められます。",
        "For personal use, you can start right now. Once you create an account, your personal Sophy will start helping with your work immediately. For team use, basic setup takes about 10 minutes, and even with inviting members, everyone can start using it within a day.",
        "如果是个人使用，可以立即开始。创建账户后，您的专属Sophy就会立即开始为您的工作提供支持。即使是团队使用，基本设置也只需约10分钟，即使邀请成员，一天之内大家就可以开始使用了。"
      ),
    },
    {
      question: t("データは安全に守られますか？", "Is my data safe and secure?", "数据安全吗？"),
      answer: t(
        "はい、安心してお使いください。あなたのデータはすべて暗号化され、厳重なセキュリティ環境で保管されます。また、あなたのデータを勝手にAIの学習に使うことは絶対にありません。あなたのプライバシーは完全に守られます。",
        "Yes, you can use it with peace of mind. All your data is encrypted and stored in a strictly secure environment. We will never use your data to train AI without permission. Your privacy is completely protected.",
        "是的，请放心使用。您的所有数据都经过加密，并存储在严格的安全环境中。此外，我们绝不会擅自将您的数据用于AI训练。您的隐私将得到完全保护。"
      ),
    },
    {
      question: t(
        "いつも使っているツールと一緒に使えますか？",
        "Can I use it with my usual tools?",
        "可以与常用工具一起使用吗？"
      ),
      answer: t(
        "はい、SlackやMicrosoft Teams、Jira、Asanaなど、よく使われているツールと一緒に使えます。他のツールとも繋げられるので、いつもの仕事の流れを変えずにソフィーを使い始められます。",
        "Yes, it works with commonly used tools like Slack, Microsoft Teams, Jira, and Asana. You can also connect it with other tools, so you can start using Sophy without changing your usual workflow.",
        "是的，可以与Slack、Microsoft Teams、Jira、Asana等常用工具一起使用。也可以连接其他工具，因此您可以在不改变平时工作流程的情况下开始使用Sophy。"
      ),
    },
    {
      question: t("無料で試せますか？", "Can I try it for free?", "可以免费试用吗？"),
      answer: t(
        "はい、14日間無料で試せます。クレジットカードの登録も必要ありません。すべての機能を自由に使ってみて、自分に合うか確かめてください。",
        "Yes, you can try it free for 14 days. No credit card needed. Feel free to use all features and see if it works for you.",
        "是的，可以免费试用14天。无需注册信用卡。请随意使用所有功能，看看是否适合您。"
      ),
    },
    {
      question: t(
        "困ったときはサポートしてもらえますか？",
        "Will I get support when I need help?",
        "遇到困难时能获得支持吗？"
      ),
      answer: t(
        "はい、安心してください。スタータープランならメールで、プロプランなら優先的にサポートします。Slackコミュニティで他のユーザーと情報交換もできますし、使い方を教えてもらえるセッションもあります。",
        "Yes, don't worry. Starter plan includes email support, and Pro plan gets priority assistance. You can also exchange tips with other users in our Slack community, and we offer sessions to help you learn how to use Sophy.",
        "是的，请放心。入门套餐可以通过电子邮件获得支持，专业套餐则可以获得优先支持。您还可以在Slack社区与其他用户交流信息，我们也提供教您如何使用的培训课程。"
      ),
    },
  ]

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  const handleLogout = async () => {
    await signOut()
    router.push("/")
  }

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400&display=swap" rel="stylesheet" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-16 xl:py-24 overflow-hidden mt-[54px] relative z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col xl:flex-row items-center gap-8 xl:gap-12">
              {/* Left: Text Content */}
              <div className="flex-1 text-center xl:text-left">
                <p className="text-base md:text-lg lg:text-xl font-medium mb-4" style={{ color: "#333" }}>
                  {t("忙しいあなたに、いつも寄り添うAI", "Your AI companion for busy moments", "忙碌时的贴心AI伙伴")}
                </p>
                <h1
                  className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6 leading-tight xl:leading-snug"
                  style={{
                    color: "#333",
                    fontFamily: "Sora, Noto Sans JP, sans-serif",
                  }}
                >
                  {language === "ja" ? (
                    <>
                      いつでもどこでも、
                      <br className="md:hidden xl:block" />
                      あなただけのソフィーと
                    </>
                  ) : language === "zh" ? (
                    <>
                      随时随地，
                      <br className="md:hidden xl:block" />
                      您专属的Sophy
                    </>
                  ) : (
                    <>
                      Your Own Sophy,
                      <br className="md:hidden xl:block" />
                      Anytime, Anywhere
                    </>
                  )}
                </h1>
                <p
                  className="text-base md:text-lg font-light mb-8 xl:mb-0 leading-relaxed xl:max-w-xl"
                  style={{ color: "#333" }}
                >
                  {language === "ja" ? (
                    <>
                      移動中や外出先でも、あなただけのAIが支えます。
                      <br />
                      タスク管理からチームとのコミュニケーションまで、
                      <br />
                      どの端末からでも、あなたのワークライフバランスを守ります。
                    </>
                  ) : language === "zh" ? (
                    "无论身处何地，您的专属AI随时为您提供支持。从任务管理到团队沟通，任何设备都能使用，守护您的工作生活平衡。"
                  ) : (
                    "Your personal AI supports you wherever you go. Manage everything from tasks to team communication on any device, protecting your work-life balance."
                  )}
                </p>

                {/* Mobile/Tablet: Sophy Image between text and button */}
                <div className="xl:hidden flex justify-center mt-8 mb-8">
                  <div className="relative w-full max-w-[320px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px]">
                    <Image
                      src="/images/sophy_m.png"
                      alt="Sophy Assistant"
                      width={600}
                      height={600}
                      className="w-full h-auto border-0 outline-none"
                      priority
                    />
                    {/* Mobile Floating Notification Cards */}
                    {notificationCards.map((card) => (
                      <div key={`${card.id}-mobile`} className={`absolute ${card.position.mobile}`}>
                        <MobileNotificationCard
                          id={card.id}
                          title={card.title}
                          message={
                            notifications[card.id as keyof typeof notifications][
                              notificationIndex[card.id as keyof typeof notificationIndex]
                            ][language]
                          }
                          icon={card.icon}
                          index={notificationIndex[card.id as keyof typeof notificationIndex]}
                          animationDirection={card.animationDirection}
                          animationDuration={card.animationDuration}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="hidden xl:flex justify-start mt-8 gap-3">
                  {/* Mac App Store */}
                  <a
                    href="https://apps.apple.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center h-[40px] px-4 bg-[#000] hover:bg-[#1a1a1a] transition-colors rounded-md"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="white">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    <span className="text-white text-sm font-medium whitespace-nowrap">
                      {t("Mac App Store", "Mac App Store", "Mac App Store")}
                    </span>
                  </a>
                  {/* Microsoft Store */}
                  <a
                    href="https://apps.microsoft.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center h-[40px] px-4 bg-[#000] hover:bg-[#1a1a1a] transition-colors rounded-md"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                    </svg>
                    <span className="text-white text-sm font-medium whitespace-nowrap">
                      {t("Microsoft Store", "Microsoft Store", "Microsoft Store")}
                    </span>
                  </a>
                </div>
              </div>

              {/* Right: Sophy Image (Desktop only) */}
              <div className="hidden xl:flex flex-1 justify-start">
                <div className="relative w-full max-w-[450px] 2xl:max-w-[550px]">
                  <Image
                    src="/images/sophy_m.png"
                    alt="Sophy Assistant"
                    width={600}
                    height={600}
                    className="w-full h-auto border-0 outline-none"
                    priority
                  />
                  {/* Desktop Floating Notification Cards */}
                  {notificationCards.map((card) => (
                    <div key={card.id} className={`absolute ${card.position.desktop}`}>
                      <DesktopNotificationCard
                        id={card.id}
                        title={card.title}
                        message={
                          notifications[card.id as keyof typeof notifications][
                            notificationIndex[card.id as keyof typeof notificationIndex]
                          ][language]
                        }
                        icon={card.icon}
                        index={notificationIndex[card.id as keyof typeof notificationIndex]}
                        animationDirection={card.animationDirection}
                        animationDuration={card.animationDuration}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Download Buttons */}
        <div className="xl:hidden flex flex-col sm:flex-row justify-center items-center gap-3 px-6 pb-8">
          {/* Mac App Store */}
          <a
            href="https://apps.apple.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center h-[48px] px-4 bg-[#000] hover:bg-[#1a1a1a] transition-colors rounded-lg w-full sm:w-auto"
          >
            <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="white">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <span className="text-white text-sm font-semibold whitespace-nowrap">Mac App Store</span>
          </a>
          {/* Microsoft Store */}
          <a
            href="https://apps.microsoft.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center h-[48px] px-4 bg-[#000] hover:bg-[#1a1a1a] transition-colors rounded-lg w-full sm:w-auto"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            <span className="text-white text-sm font-semibold whitespace-nowrap">Microsoft Store</span>
          </a>
        </div>

        {/* Integrations Marquee */}
        <section className="py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: "#111" }}>
              {t("いつものツールと、すぐつながる", "Works with your favorite tools", "与您常用的工具无缝连接")}
            </h2>
          </div>

          <div className="relative overflow-hidden">
            {/* Fade edges */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 z-10"
              style={{
                background: "linear-gradient(to right, white, transparent)",
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 z-10"
              style={{
                background: "linear-gradient(to left, white, transparent)",
              }}
            />

            <div className="flex w-max animate-marquee-scroll">
              {[0, 1].map((set) => (
                <div key={set} className="flex shrink-0 gap-4 md:gap-6 pr-4 md:pr-6">
                  {allTools
                    .map((tool) => {
                      const displayName = tool.name === "メール" ? t("メール", "Email", "邮件") : tool.name
                      return { ...tool, displayName }
                    })
                    .map((tool) => (
                      <div
                        key={`${set}-${tool.name}`}
                        className="flex items-center gap-2.5 md:gap-3 px-5 md:px-6 py-2.5 md:py-3 rounded-full border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all shrink-0"
                      >
                        <Image
                          src={tool.logo}
                          alt={tool.name}
                          width={24}
                          height={24}
                          className="w-5 h-5 md:w-6 md:h-6 object-contain"
                        />
                        <span
                          className="text-sm md:text-[15px] font-medium whitespace-nowrap"
                          style={{ color: "#333" }}
                        >
                          {tool.displayName}
                        </span>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon Section */}
        <section
          className="py-32 md:py-48 px-6 relative overflow-hidden cursor-pointer md:cursor-default"
          onClick={() => setIsComingSoonActive(!isComingSoonActive)}
        >
          {/* Subtle grid background extending into hero section */}
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{
              top: "-300px",
              backgroundImage: `
              linear-gradient(to right, #000 1px, transparent 1px),
              linear-gradient(to bottom, #000 1px, transparent 1px)
            `,
              backgroundSize: "60px 60px",
              backgroundPosition: "-0.5px -0.5px",
              opacity: 0.05,
              maskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.8) 50%, black 65%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.8) 50%, black 65%)",
            }}
          />

          {/* Animated particles - moving along grid lines */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Particles following various paths */}
            {/* 21 particles - Fibonacci number */}
            {[
              {
                anim: "path1",
                delay: 0,
                size: 2.5,
                color: "rgba(20, 184, 166, 0.8)",
              },
              {
                anim: "path2",
                delay: 0.5,
                size: 2,
                color: "rgba(45, 212, 191, 0.7)",
              },
              {
                anim: "path3",
                delay: 1.0,
                size: 2,
                color: "rgba(34, 211, 238, 0.7)",
              },
              {
                anim: "path4",
                delay: 0.25,
                size: 2.5,
                color: "rgba(94, 234, 212, 0.8)",
              },
              {
                anim: "path5",
                delay: 0.75,
                size: 2,
                color: "rgba(52, 211, 153, 0.7)",
              },
              {
                anim: "path6",
                delay: 1.25,
                size: 2.5,
                color: "rgba(20, 184, 166, 0.7)",
              },
              {
                anim: "path7",
                delay: 0.1,
                size: 2,
                color: "rgba(45, 212, 191, 0.8)",
              },
              {
                anim: "path8",
                delay: 0.6,
                size: 2.5,
                color: "rgba(34, 211, 238, 0.8)",
              },
              {
                anim: "path9",
                delay: 1.1,
                size: 2,
                color: "rgba(94, 234, 212, 0.7)",
              },
              {
                anim: "path10",
                delay: 0.35,
                size: 2.5,
                color: "rgba(52, 211, 153, 0.8)",
              },
              {
                anim: "path11",
                delay: 0.85,
                size: 2,
                color: "rgba(20, 184, 166, 0.7)",
              },
              {
                anim: "path12",
                delay: 1.35,
                size: 2,
                color: "rgba(45, 212, 191, 0.7)",
              },
              {
                anim: "path13",
                delay: 1.5,
                size: 2.5,
                color: "rgba(34, 211, 238, 0.75)",
              },
              {
                anim: "path14",
                delay: 0.15,
                size: 2,
                color: "rgba(94, 234, 212, 0.7)",
              },
              {
                anim: "path15",
                delay: 0.65,
                size: 2.5,
                color: "rgba(52, 211, 153, 0.75)",
              },
              {
                anim: "path16",
                delay: 1.15,
                size: 2,
                color: "rgba(20, 184, 166, 0.7)",
              },
              {
                anim: "path17",
                delay: 0.4,
                size: 2.5,
                color: "rgba(45, 212, 191, 0.8)",
              },
              {
                anim: "path18",
                delay: 0.9,
                size: 2,
                color: "rgba(34, 211, 238, 0.7)",
              },
              {
                anim: "path19",
                delay: 1.4,
                size: 2.5,
                color: "rgba(94, 234, 212, 0.75)",
              },
              {
                anim: "path20",
                delay: 0.2,
                size: 2,
                color: "rgba(52, 211, 153, 0.7)",
              },
              {
                anim: "path21",
                delay: 0.7,
                size: 2.5,
                color: "rgba(20, 184, 166, 0.8)",
              },
            ].map((p, i) => (
              <div
                key={i}
                className="absolute rounded-full blur-[0.5px]"
                style={{
                  width: `${p.size * 4}px`,
                  height: `${p.size * 4}px`,
                  backgroundColor: p.color,
                  animation: `${p.anim} ${6 + i * 0.5}s linear infinite`,
                  animationDelay: `${p.delay}s`,
                  boxShadow: `0 0 ${p.size * 6}px ${p.size * 2}px ${p.color.replace("0.7", "0.4").replace("0.8", "0.5")}`,
                  transform: "translate(-50%, -50%)",
                }}
              />
            ))}
          </div>

          {/* CSS Animations - particles from all directions */}
          <style jsx>{`
            /* 左から5個 */
            @keyframes path1 {
              0% {
                top: 60px;
                left: -10px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              30% {
                top: 60px;
                left: 420px;
              }
              60% {
                top: 180px;
                left: 420px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 180px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path2 {
              0% {
                top: 180px;
                left: -10px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 180px;
                left: 540px;
              }
              70% {
                top: 300px;
                left: 540px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 300px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path3 {
              0% {
                top: 300px;
                left: -10px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 300px;
                left: 480px;
              }
              75% {
                top: 120px;
                left: 480px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 480px;
                opacity: 0;
              }
            }
            @keyframes path4 {
              0% {
                top: 420px;
                left: -10px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 420px;
                left: 360px;
              }
              70% {
                top: 240px;
                left: 360px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 240px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path5 {
              0% {
                top: 120px;
                left: -10px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              30% {
                top: 120px;
                left: 600px;
              }
              60% {
                top: 360px;
                left: 600px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 100%;
                left: 600px;
                opacity: 0;
              }
            }
            /* 右から6個 */
            @keyframes path6 {
              0% {
                top: 60px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 60px;
                left: 300px;
              }
              70% {
                top: 240px;
                left: 300px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 240px;
                left: -10px;
                opacity: 0;
              }
            }
            @keyframes path7 {
              0% {
                top: 180px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 180px;
                left: 180px;
              }
              70% {
                top: 60px;
                left: 180px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 180px;
                opacity: 0;
              }
            }
            @keyframes path8 {
              0% {
                top: 300px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 300px;
                left: 120px;
              }
              70% {
                top: 420px;
                left: 120px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 100%;
                left: 120px;
                opacity: 0;
              }
            }
            @keyframes path9 {
              0% {
                top: 420px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 420px;
                left: 240px;
              }
              75% {
                top: 180px;
                left: 240px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 180px;
                left: -10px;
                opacity: 0;
              }
            }
            @keyframes path10 {
              0% {
                top: 240px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 240px;
                left: 360px;
              }
              70% {
                top: 60px;
                left: 360px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 360px;
                opacity: 0;
              }
            }
            @keyframes path11 {
              0% {
                top: 360px;
                left: 100%;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 360px;
                left: 60px;
              }
              70% {
                top: 180px;
                left: 60px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 180px;
                left: -10px;
                opacity: 0;
              }
            }
            /* 上から5個 */
            @keyframes path12 {
              0% {
                top: -10px;
                left: 120px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 300px;
                left: 120px;
              }
              70% {
                top: 300px;
                left: 480px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 300px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path13 {
              0% {
                top: -10px;
                left: 300px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 240px;
                left: 300px;
              }
              75% {
                top: 240px;
                left: 540px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 100%;
                left: 540px;
                opacity: 0;
              }
            }
            @keyframes path14 {
              0% {
                top: -10px;
                left: 480px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 180px;
                left: 480px;
              }
              70% {
                top: 180px;
                left: 180px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 180px;
                left: -10px;
                opacity: 0;
              }
            }
            @keyframes path15 {
              0% {
                top: -10px;
                left: 600px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 360px;
                left: 600px;
              }
              70% {
                top: 360px;
                left: 300px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 100%;
                left: 300px;
                opacity: 0;
              }
            }
            @keyframes path16 {
              0% {
                top: -10px;
                left: 420px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 120px;
                left: 420px;
              }
              75% {
                top: 120px;
                left: 60px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 120px;
                left: -10px;
                opacity: 0;
              }
            }
            /* 下から5個 */
            @keyframes path17 {
              0% {
                top: 100%;
                left: 180px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 240px;
                left: 180px;
              }
              70% {
                top: 240px;
                left: 540px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 240px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path18 {
              0% {
                top: 100%;
                left: 360px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 120px;
                left: 360px;
              }
              75% {
                top: 120px;
                left: 600px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 600px;
                opacity: 0;
              }
            }
            @keyframes path19 {
              0% {
                top: 100%;
                left: 540px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 180px;
                left: 540px;
              }
              70% {
                top: 180px;
                left: 120px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 120px;
                opacity: 0;
              }
            }
            @keyframes path20 {
              0% {
                top: 100%;
                left: 60px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              40% {
                top: 60px;
                left: 60px;
              }
              75% {
                top: 60px;
                left: 420px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: 60px;
                left: 100%;
                opacity: 0;
              }
            }
            @keyframes path21 {
              0% {
                top: 100%;
                left: 480px;
                opacity: 0;
              }
              5% {
                opacity: 1;
              }
              35% {
                top: 300px;
                left: 480px;
              }
              70% {
                top: 300px;
                left: 240px;
              }
              95% {
                opacity: 1;
              }
              100% {
                top: -10px;
                left: 240px;
                opacity: 0;
              }
            }
            @keyframes glitch {
              0% {
                transform: translate(0);
                text-shadow: none;
              }
              2% {
                transform: translate(-3px, 2px) skewX(-2deg);
                text-shadow:
                  2px 0 #0ff,
                  -2px 0 #f0f;
              }
              4% {
                transform: translate(3px, -1px) skewX(2deg);
                text-shadow:
                  -2px 0 #0ff,
                  2px 0 #f0f;
              }
              6% {
                transform: translate(-2px, -2px);
                text-shadow:
                  3px 0 #0ff,
                  -3px 0 #f0f;
              }
              8% {
                transform: translate(2px, 2px) skewX(-1deg);
                text-shadow:
                  -1px 0 #0ff,
                  1px 0 #f0f;
              }
              10% {
                transform: translate(-3px, 1px) skewX(1deg);
                text-shadow:
                  2px 0 #0ff,
                  -2px 0 #f0f;
              }
              12% {
                transform: translate(1px, -2px);
                text-shadow:
                  -3px 0 #0ff,
                  3px 0 #f0f;
              }
              14% {
                transform: translate(0);
                text-shadow: none;
              }
              100% {
                transform: translate(0);
                text-shadow: none;
              }
            }
            .animate-glitch {
              animation: glitch 0.3s ease-in-out infinite;
            }
            .group:hover .glitch-on-hover,
            .group:active .glitch-on-hover {
              animation: glitch 0.15s ease-in-out infinite;
            }
          `}</style>

          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Coming Soon with Sand Effect + Hidden Message */}
            <div className="relative cursor-pointer group mb-8">
              {/* Hidden message - appears when Coming Soon dissolves with electric effect */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 delay-200 z-50 ${isComingSoonActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              >
                <span
                  className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-teal-500 ${isComingSoonActive ? "animate-glitch" : "glitch-on-hover"}`}
                  style={{
                    textShadow:
                      "0 0 40px rgba(20, 184, 166, 0.6), 0 0 80px rgba(20, 184, 166, 0.4), 0 0 120px rgba(20, 184, 166, 0.2)",
                  }}
                >
                  Spring 2026
                </span>
              </div>

              {/* Coming Soon text */}
              <h2
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter relative"
                style={{ color: "#000" }}
              >
                {/* Fixed seed values for each character to avoid hydration mismatch */}
                {[
                  { char: "C", tx: -80, ty: 120, r: -35 },
                  { char: "o", tx: 60, ty: 80, r: 25 },
                  { char: "m", tx: -40, ty: 140, r: -15 },
                  { char: "i", tx: 90, ty: 65, r: 40 },
                  { char: "n", tx: -55, ty: 110, r: -28 },
                  { char: "g", tx: 75, ty: 95, r: 32 },
                  { char: " ", tx: 0, ty: 50, r: 0 },
                  { char: "S", tx: -70, ty: 130, r: -42 },
                  { char: "o", tx: 85, ty: 75, r: 38 },
                  { char: "o", tx: -45, ty: 145, r: -20 },
                  { char: "n", tx: 65, ty: 85, r: 30 },
                ].map((item, i) => (
                  <span
                    key={i}
                    className={`inline-block transition-all duration-700 ease-out ${isComingSoonActive ? "opacity-0" : "group-hover:opacity-0"}`}
                    style={
                      {
                        transitionDelay: `${i * 30}ms`,
                        ["--tx" as string]: `${item.tx}px`,
                        ["--ty" as string]: `${item.ty}px`,
                        ["--r" as string]: `${item.r}deg`,
                      } as React.CSSProperties
                    }
                  >
                    <span
                      className={`inline-block transition-all duration-700 ease-out ${isComingSoonActive ? "translate-x-[var(--tx)] translate-y-[var(--ty)] rotate-[var(--r)] scale-0" : "group-hover:translate-x-[var(--tx)] group-hover:translate-y-[var(--ty)] group-hover:rotate-[var(--r)] group-hover:scale-0"}`}
                      style={{ transitionDelay: `${i * 30}ms` }}
                    >
                      {item.char === " " ? "\u00A0" : item.char}
                    </span>
                  </span>
                ))}
              </h2>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
